import { useContext, useEffect, useState } from "react";
import { AppContext, endpointFor } from "../../context/AppStore";

/**
 * Every tool's way to a Solana mint's official logo: the image its metadata
 * names, as DAS indexed it. One cache for the page view; the mints asked
 * for in the same tick share one getAssetBatch call, through Solstack's own
 * RPC for the app's network (a custom RPC may have no DAS); pages outside the
 * app (the marketing site) read mainnet.
 * SOL and wSOL show Solana's own logo (public/logos). Only React, fetch and
 * the app's network setting, so TokenLogo stays light wherever it's used.
 */
export const SOL_MINT = "So11111111111111111111111111111111111111112";
export const SOL_LOGO = "/logos/coins/sol.png";
const PINNED = { [SOL_MINT]: { images: [SOL_LOGO], symbol: "SOL", name: "Solana" } };
const NONE = { images: [], symbol: "", name: "" };

const found = new Map(); // `${endpoint} ${mint}` → { images, symbol, name }
const queues = new Map(); // endpoint → { mints: Set, waiters: [[mint, resolve]] }

// The indexer's CDN copy first (fast, and a raster even when the original
// is an SVG), then the metadata's own link, then any other file it lists.
const imagesOf = (it) =>
  [
    ...(it?.content?.files || []).map((f) => f?.cdn_uri),
    it?.content?.links?.image,
    ...(it?.content?.files || []).map((f) => f?.uri),
  ].filter((u, i, all) => typeof u === "string" && u && all.indexOf(u) === i);

async function assetBatch(endpoint, ids) {
  const out = {};
  for (let i = 0; i < ids.length; i += 1000) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "getAssetBatch", params: { ids: ids.slice(i, i + 1000) } }),
      });
      // eslint-disable-next-line no-await-in-loop
      const { result } = await res.json();
      (result || []).forEach((it) => {
        if (it?.id) {
          out[it.id] = { images: imagesOf(it), symbol: it.content?.metadata?.symbol || "", name: it.content?.metadata?.name || "" };
        }
      });
    } catch {
      /* no DAS on this RPC, or a blip: these stay without a logo */
    }
  }
  return out;
}

function lookup(endpoint, mint) {
  const key = `${endpoint} ${mint}`;
  if (found.has(key)) return Promise.resolve(found.get(key));
  return new Promise((resolve) => {
    let q = queues.get(endpoint);
    if (!q) {
      q = { mints: new Set(), waiters: [] };
      queues.set(endpoint, q);
      setTimeout(async () => {
        queues.delete(endpoint);
        const meta = await assetBatch(endpoint, [...q.mints]);
        q.mints.forEach((m) => found.set(`${endpoint} ${m}`, meta[m] || NONE));
        q.waiters.forEach(([m, done]) => done(found.get(`${endpoint} ${m}`)));
      }, 0);
    }
    q.mints.add(mint);
    q.waiters.push([mint, resolve]);
  });
}

/** The RPC the lookups go through: Solstack's own, for the app's network. */
const useEndpoint = () => {
  const [app] = useContext(AppContext) || [];
  return endpointFor(app?.currentNetwork || "mainnet", {}) || "/api/rpc/mainnet";
};

/** A mint's DAS record ({ images, symbol, name }), empty until it arrives. Pass null to skip. */
export function useMintMeta(mint) {
  const endpoint = useEndpoint();
  const known = mint ? PINNED[mint] || found.get(`${endpoint} ${mint}`) : null;
  const [, setVersion] = useState(0);
  useEffect(() => {
    if (!mint || known) return undefined;
    let live = true;
    lookup(endpoint, mint).then(() => live && setVersion((v) => v + 1));
    return () => {
      live = false;
    };
  }, [endpoint, mint, known]);
  return known || NONE;
}

/** A mint's official logo URLs, best first; empty until known (or if it has none). */
export const useMintLogo = (mint) => useMintMeta(mint).images;
